import React from "react";
import * as ReactDOM from "react-dom/client";
import "./landing-shared.entry.jsx";
const SiteNav = globalThis.SiteNav ?? globalThis.window?.SiteNav;
const SiteFooter = globalThis.SiteFooter ?? globalThis.window?.SiteFooter;
// Pricing page
const { useState } = React;

const Arrow = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square"/>
  </svg>
);

const PLANS = [
  {
    key: 'free',
    name: 'Free',
    monthly: 0,
    yearly: 0,
    desc: 'Try named.network on your own setup — protect one app without opening ports.',
    cta: 'Get started',
    href: 'https://app.named.network/signup',
    primary: false,
    featured: false,
    features: [
      { t: '2 devices' },
      { t: '1 protected app' },
      { t: 'Free privacy/exposure tools' },
      { t: 'Community support' },
      { t: 'First-device / first-app onboarding' },
    ],
  },
  {
    key: 'personal',
    name: 'Personal',
    monthly: 3,
    yearly: 29,
    desc: 'For personal homelabs and self-hosted tools — more devices, more apps, light sharing.',
    cta: 'Start Personal',
    href: 'https://app.named.network/signup?plan=personal',
    primary: false,
    featured: false,
    features: [
      { t: '10 devices' },
      { t: '3 protected apps' },
      { t: 'Secure DNS when shipped' },
      { t: 'Basic sharing' },
      { t: '50 GB traffic per month' },
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    monthly: 7,
    yearly: 69,
    desc: 'For power users, freelancers, and heavier solo setups — better sharing and operational visibility.',
    cta: 'Start Pro',
    href: 'https://app.named.network/signup?plan=pro',
    primary: false,
    featured: false,
    features: [
      { t: '50 devices' },
      { t: '10 protected apps' },
      { t: 'Custom named.network subdomains' },
      { t: 'Temporary sharing' },
      { t: 'Server access when Private Network ships' },
      { t: '250 GB traffic per month' },
      { t: 'Basic access logs' },
    ],
  },
  {
    key: 'team',
    name: 'Team',
    monthly: 19,
    perUser: 3,
    desc: 'Share private apps with teammates, manage who can open what, and keep access organized.',
    cta: 'Start Team',
    href: 'https://app.named.network/signup?plan=team',
    primary: true,
    featured: true,
    features: [
      { t: '5 users included', strong: true },
      { t: '100 devices' },
      { t: '25 protected apps' },
      { t: 'App sharing and invitations' },
      { t: 'Access requests and approvals' },
      { t: 'Member roles, scoped groups and tags' },
      { t: 'Access-rule management' },
      { t: 'Expiry editing and revoke' },
      { t: 'Person & workspace-level access investigation' },
      { t: 'Admin-assisted teammate recovery' },
      { t: 'Audit-backed access history' },
      { t: '1 TB traffic per month' },
    ],
  },
  {
    key: 'business',
    name: 'Business',
    custom: true,
    desc: 'For teams that need automation, agent access, advanced policy, and operator controls.',
    cta: 'Contact us',
    href: 'contact.html',
    primary: false,
    featured: false,
    features: [
      { t: 'Everything in Team', strong: true },
      { t: 'Hosted MCP' },
      { t: 'Business API tokens' },
      { t: 'AI / automation access to scoped tools' },
      { t: 'Approval-gated mutating workflows' },
      { t: 'Public Gateway / bounded egress' },
      { t: 'Advanced Secure DNS operations' },
      { t: 'Advanced Private Network / server-access' },
      { t: 'Customer-owned Exit Nodes' },
      { t: 'Exit-node fair-use and abuse controls' },
      { t: 'Higher-touch rollout / support' },
    ],
  },
];

function Hero({ billing, setBilling }) {
  return (
    <section className="section section-dark pp-hero">
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <span className="pp-eyebrow"><span className="eb-dot"></span>PRICING · CURRENT PLANS</span>
        <h1>Start free. Grow into <em>real daily setups.</em><br/>Pay for collaboration when you need it.</h1>
        <p className="lead">No &ldquo;contact us&rdquo; mystery boxes for the things solo developers and small teams actually need. Custom pricing is reserved for Business, where it earns its keep.</p>
        <div className="pp-toggle" role="tablist" aria-label="Billing period">
          <button className={billing === 'monthly' ? 'on' : ''} onClick={() => setBilling('monthly')}>Monthly</button>
          <button className={billing === 'yearly' ? 'on' : ''} onClick={() => setBilling('yearly')}>
            Yearly <span className="save">SAVE ~20%</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan, billing }) {
  const price = (() => {
    if (plan.custom) return <div className="custom">Custom</div>;
    if (plan.monthly === 0) {
      return (
        <>
          <span className="num">$0</span>
          <span className="unit">/ month</span>
          <span className="yearly">No card required.</span>
        </>
      );
    }
    const monthly = plan.monthly;
    const yearly = plan.yearly;
    const monthlyFromYearly = Math.round((yearly / 12) * 100) / 100;
    const isYear = billing === 'yearly';
    if (!yearly) {
      return (
        <>
          <span className="num">${monthly}</span>
          <span className="unit">/ month{plan.perUser ? ' · base' : ''}</span>
          <span className="yearly">Monthly pricing{plan.perUser ? ` · + $${plan.perUser}/user` : ''}</span>
        </>
      );
    }
    const main = isYear ? monthlyFromYearly : monthly;
    return (
      <>
        <span className="num">${main}</span>
        <span className="unit">/ month{plan.perUser ? ' · base' : ''}</span>
        <span className="yearly">
          {isYear
            ? <>Billed yearly · <strong>${yearly}/year</strong>{plan.perUser ? ` + $${plan.perUser}/user` : ''}</>
            : <>Or <strong>${yearly}/year</strong>{plan.perUser ? ` + $${plan.perUser}/user` : ''}</>}
        </span>
      </>
    );
  })();

  return (
    <div className={`plan ${plan.featured ? 'featured' : ''}`}>
      <div className="ptag">{plan.featured ? 'COLLABORATION' : plan.name.toUpperCase()}</div>
      <h2>{plan.name}</h2>
      <div className="price">{price}</div>
      <div className="desc">{plan.desc}</div>
      <a href={plan.href} className={`cta-btn ${plan.primary ? 'primary' : ''}`}>
        <span>{plan.cta}</span><Arrow />
      </a>
      <div className="feat-label">What&rsquo;s included</div>
      <ul>
        {plan.features.map((f, i) => (
          <li key={i}>
            <span className="tick">+</span>
            <span>{f.strong ? <strong>{f.t}</strong> : f.t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Plans({ billing }) {
  return (
    <section className="plans">
      <div className="dark-grid"></div>
      <div className="container">
        <div className="plan-grid">
          {PLANS.map(p => <PlanCard key={p.key} plan={p} billing={billing} />)}
        </div>
        <div style={{ marginTop: 24, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dark-muted)', letterSpacing: '0.04em', textAlign: 'center' }}>
          All prices in USD · Cancel any time · Yearly pricing is shown where it is currently defined
        </div>
      </div>
    </section>
  );
}

function Boundary() {
  return (
    <section className="boundary">
      <div className="container">
        <div className="boundary-card">
          <div className="lbl">THE LINE WE DRAW</div>
          <h3>Team is collaboration. <em>Business is operator depth.</em></h3>
          <div className="pair">
            <div>
              <strong>Team</strong>
              Share Protected Apps with people you trust. Roles, groups, access requests, audit-backed history, admin-assisted recovery. Built around the workflow of <em>managing who can open what</em>.
            </div>
            <div>
              <strong>Business</strong>
              Everything in Team, plus hosted MCP, AI/automation surfaces with approval gating, bounded egress through the Public Gateway, and customer-owned Exit Nodes with clear abuse and support boundaries.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Compare() {
  const rows = [
    { group: 'Workspace', items: [
      ['Devices', '2', '10', '50', '100', 'Custom'],
      ['Protected apps', '1', '3', '10', '25', 'Custom'],
      ['Users included', '1', '1', '1', '5', 'Custom'],
      ['Additional users', '—', '—', '—', '$3/user/mo', 'Negotiated'],
      ['Traffic / month', '—', '50 GB', '250 GB', '1 TB', 'Custom'],
      ['Custom subdomains', '—', '—', 'Yes', 'Yes', 'Yes'],
    ]},
    { group: 'Sharing & access', items: [
      ['Basic sharing', '—', 'Yes', 'Yes', 'Yes', 'Yes'],
      ['Temporary sharing', '—', '—', 'Yes', 'Yes', 'Yes'],
      ['Invitations & access requests', '—', '—', '—', 'Yes', 'Yes'],
      ['Member roles & groups', '—', '—', '—', 'Yes', 'Yes'],
      ['Scoped tags & access rules', '—', '—', '—', 'Yes', 'Yes'],
      ['Expiry edit, revoke, recovery', '—', '—', '—', 'Yes', 'Yes'],
    ]},
    { group: 'Visibility & investigation', items: [
      ['Basic access logs', '—', '—', 'Yes', 'Yes', 'Yes'],
      ['Person-level access investigation', '—', '—', '—', 'Yes', 'Yes'],
      ['Workspace-level access investigation', '—', '—', '—', 'Yes', 'Yes'],
      ['Audit-backed access history', '—', '—', '—', 'Yes', 'Yes'],
    ]},
    { group: 'Advanced services', items: [
      ['Secure DNS', '—', 'Yes', 'Yes', 'Yes', 'Advanced ops'],
      ['Exit Nodes', '—', '—', '—', '—', 'Customer-owned'],
      ['Private Network / server access', '—', '—', 'Yes', 'Yes', 'Advanced ops'],
    ]},
    { group: 'Business surfaces', items: [
      ['Hosted MCP', '—', '—', '—', '—', 'Yes'],
      ['Business API tokens', '—', '—', '—', '—', 'Yes'],
      ['AI / automation scoped tools', '—', '—', '—', '—', 'Yes'],
      ['Approval-gated mutating workflows', '—', '—', '—', '—', 'Yes'],
      ['Public Gateway / bounded egress', '—', '—', '—', '—', 'Yes'],
    ]},
    { group: 'Support', items: [
      ['Community support', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'],
      ['Workspace change help', '—', '—', '—', '—', 'Yes'],
      ['Higher-touch rollout', '—', '—', '—', '—', 'Yes'],
    ]},
  ];

  const cell = (v) => {
    if (v === 'Yes') return <span className="v-yes">●</span>;
    if (v === '—') return <span className="v-no">—</span>;
    if (v && v.toString().startsWith('Advanced')) return <span className="v-yes">{v}</span>;
    return <span>{v}</span>;
  };

  return (
    <section className="compare-section">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Compare every plan</span>
          <h2>The honest breakdown.</h2>
          <p>Everything we&rsquo;ll ship on. Nothing we won&rsquo;t.</p>
        </div>
        <div className="ct-wrap">
          <table className="ct">
            <thead>
              <tr>
                <th></th>
                <th><span className="plan-name">Free</span>$0</th>
                <th><span className="plan-name">Personal</span>$3 / mo</th>
                <th><span className="plan-name">Pro</span>$7 / mo</th>
                <th className="feat"><span className="plan-name">Team</span>$19 + $3/user</th>
                <th><span className="plan-name">Business</span>Custom</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g, gi) => (
                <React.Fragment key={gi}>
                  <tr className="group"><td colSpan={6}>{g.group}</td></tr>
                  {g.items.map((row, ri) => (
                    <tr key={ri}>
                      <td className="row-label">{row[0]}</td>
                      <td>{cell(row[1])}</td>
                      <td>{cell(row[2])}</td>
                      <td>{cell(row[3])}</td>
                      <td className="feat-col">{cell(row[4])}</td>
                      <td>{cell(row[5])}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function Notes() {
  const items = [
    { n: '01', t: 'Business is custom on purpose.', d: 'Business includes the deepest AI, automation, DNS, network, customer-owned Exit Nodes, and bounded-egress surfaces. We keep that as a conversation instead of pretending one flat self-serve card fits every deployment.', meta: 'Hosted MCP · gateway · exit nodes' },
    { n: '02', t: 'Exit Nodes are Business-only.', d: 'The supported Exit Nodes promise is customer-owned trusted-device egress for Business workspaces. It is not included in Free, Personal, Pro, or Team packaging, and it is not a shared public VPN pool.', meta: 'No overclaiming' },
    { n: '03', t: 'We do not publish enterprise promises we cannot stand behind yet.', d: 'SSO, SCIM, dedicated gateways, dedicated static IPs, custom regions, and formal SLA language stay out of default pricing claims until they are genuinely ready.', meta: 'Under-promise > over-promise' },
  ];
  return (
    <section className="addons">
      <div className="container">
        <div className="section-h">
          <span className="section-label on-light">Notes</span>
          <h2>Three pricing rules we keep visible.</h2>
          <p>The packaging line matters as much as the price.</p>
        </div>
        <div className="addon-grid">
          {items.map(a => (
            <div key={a.n} className="addon">
              <div className="ic">{a.n}</div>
              <h4>{a.t}</h4>
              <p>{a.d}</p>
              <div className="ap">{a.meta}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: 'Is the Free plan a trial?', a: 'No — it stays free. It\u2019s for trying named.network on your own setup, protecting one real app, and seeing if the workflow fits. Two devices, one protected app, community support. Use it as long as it covers what you\u2019re doing.' },
    { q: 'When should I move from Personal to Pro?', a: 'When you\u2019re running more services and more devices than Personal&rsquo;s limits comfortably allow, or when you want temporary sharing, custom subdomains, and basic access logs. Pro is the strong solo / consultant tier.' },
    { q: 'When is Team the right plan?', a: 'The moment a second person needs to open a protected app and you want to manage who can do that. Team is the first true collaboration plan — invitations, roles, access requests, audit-backed history, and admin-assisted recovery.' },
    { q: 'What does Business unlock that Team doesn\u2019t?', a: 'Hosted MCP, Business API tokens, AI / automation access to scoped tools, approval-gated mutating workflows, the Public Gateway for bounded egress, and customer-owned Exit Nodes for trusted-device egress. Exit Nodes carry fair-use and abuse controls, supported-host boundaries, and higher-touch rollout / support. Team is collaboration; Business is operator depth.' },
    { q: 'Why isn\u2019t Business self-serve?', a: 'Because the surfaces Business unlocks — automation, bounded egress, advanced policy — benefit from a real rollout conversation. We keep that tier custom instead of pretending one default setup fits every advanced deployment.' },
    { q: 'What about SSO, SCIM, SLA, dedicated infra?', a: 'We\u2019re not making those claims yet — they are not finalized launch promises. If you have specific requirements, contact us and we&rsquo;ll be straight with you about what&rsquo;s genuinely available today.' },
    { q: 'What happens if I hit my traffic cap?', a: 'Traffic caps are part of the current package shape, but we are not publishing a hard public overage schedule here. If your usage starts to outgrow the plan, contact us and we&rsquo;ll help you move to the right tier.' },
    { q: 'Can I cancel or downgrade?', a: 'Yes. Paid changes should follow the normal billing-period boundary, and downgrade behavior should preserve configuration rather than silently deleting resources. If you need help with a change, contact us and we&rsquo;ll walk you through it.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="section section-light">
      <div className="container-narrow">
        <div className="section-h">
          <span className="section-label on-light">FAQ</span>
          <h2>Eight common questions.</h2>
          <p>Anything not covered? Email us &mdash; we&rsquo;ll add it here.</p>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-item ${open === i ? 'open' : ''}`}>
              <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{f.q}</span><span className="plus">+</span>
              </button>
              <div className="faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="section section-dark" style={{ padding: '128px 0' }}>
      <div className="dark-grid"></div>
      <div className="dark-glow"></div>
      <div className="container">
        <div className="cta-final">
          <span className="section-label">Get started</span>
          <h2>Start free. <em>Upgrade only</em><br/>when the workflow earns it.</h2>
          <p>Two devices and one protected app, free forever. Move up when you have more apps, more devices, or more people who need to open them.</p>
          <div className="ctas" style={{ marginTop: 36 }}>
            <a href="https://app.named.network/signup" className="btn btn-accent btn-lg"><span>Create a workspace <Arrow /></span></a>
            <a href="/contact" className="btn btn-on-dark btn-lg"><span>Contact us</span></a>
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  const [billing, setBilling] = useState('monthly');
  return (
    <>
      <SiteNav active="pricing" />
      <Hero billing={billing} setBilling={setBilling} />
      <Plans billing={billing} />
      <Boundary />
      <Compare />
      <Notes />
      <FAQ />
      <CTA />
      <SiteFooter accent="pricing" />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

